<template>
  <Block class="w-full relative">
    <div class="flex flex-col w-full gap-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <img
            :src="loanTokenImg"
            alt=""
            class="h-10 w-10 rounded-full"
          />
          <h1 class="text-lg font-semibold flex items-center gap-2">
            <a
              :href="chartLink"
              target="_blank"
              rel="noopener noreferrer"
            >
              #{{ loan.tokenId }}
              {{ collateralTokenInfo.symbol }}
            </a>
            <!-- <img
              v-if="isExternalPool"
              class="h-10 w-10"
              src="/img/hlp.png"
            /> -->
            <div
              v-if="isExternalPool"
              class="border px-1"
              style="border-radius: 10px"
            >
              <small>HLP</small>
            </div>
          </h1>
        </div>
        <p class="text-xs">LTV: {{ ltvPerc }}%</p>
      </div>
      <div
        class="rounded-2xl flex flex-col gap-3 overflow-hidden relative bg-block-4 border border-block-2 p-5 w-full"
      >
        <RowText
          title="Borrower"
          :value="borrowerText"
          :link="borrowerLink"
          :danger="!isLoanHealthy"
          :success="isLoanHealthy"
          reverse
        />
        <RowText
          title="Deposited"
          :value="`${collateralAmountFormatted} ${collateralTokenInfo.symbol} ($${collateralAmountUSDFormatted})`"
          :danger="!isLoanHealthy"
          :success="isLoanHealthy"
          reverse
        />
        <RowText
          title="Borrowed"
          :value="`${borrowAmountFormatted} ${nativeCurrencySymbol} ($${borrowAmountUSDFormatted})`"
          :danger="!isLoanHealthy"
          :success="isLoanHealthy"
          reverse
        />
        <RowText
          title="Fees Accumulated"
          :value="`${feesFormatted} ${nativeCurrencySymbol} ($${feesUSDFormatted})`"
          :danger="!isLoanHealthy"
          :success="isLoanHealthy"
          reverse
        />
        <div
          class="flex items-center gap-2 border-t pt-3 mt-1 border-opacity-10"
        >
          <Bar
            :percentage="Number(ltvPerc)"
            :danger="!isLoanHealthy"
          />
          <span class="text-xs">{{ ltvPerc }}%</span>
        </div>
      </div>
      <div
        v-if="doesUserOwnLoan"
        class="grid lg:grid-cols-4 grid-cols-1 gap-2 w-full mt-2"
      >
        <Button
          v-if="hasBorrowedETH"
          :text="`Pay Back ${nativeCurrencySymbol}`"
          secondary
          small
          @click="openModal(false, false, true, false)"
        />
        <Button
          v-else
          text="Close Loan"
          secondary
          small
          @click="closeLoan"
        />
        <Button
          :text="`Deposit ${collateralTokenInfo.symbol}`"
          primary
          small
          @click="openModal(false, true, false, false)"
        />
        <Button
          :text="`Withdraw ${collateralTokenInfo.symbol}`"
          tertiary
          small
          @click="openModal(false, false, false, true)"
        />
        <Button
          :text="`Borrow ${nativeCurrencySymbol}`"
          danger
          small
          @click="openModal(true, false, false, false)"
        />
      </div>
    </div>
    <LoanModal
      v-if="isModalOpen"
      :lending-contract="loan.lendingPoolContract"
      :token-id="loan.tokenId"
      :borrow-only="borrowOnly"
      :deposit-only="depositOnly"
      :pay-only="payOnly"
      :withdraw-only="withdrawOnly"
      :already-deposit-amount="depositAmountNoDecimals"
      :already-borrow-amount="borrowAmountNoDecimals"
      @close="isModalOpen = false"
    />
    <Loading v-if="isLoading" />
  </Block>
</template>

<script>
import BigNumber from "bignumber.js";
import { mapActions, mapGetters, mapState } from "vuex";
import Block from "../global/Block.vue";
import RowText from "../global/RowText.vue";
import Button from "../global/Button.vue";
import LoanModal from "../loan/LoanModal.vue";
import Loading from "../global/Loading.vue";
import { calculateAPRFees } from "../../factories/LoanUtils";
import {
  formatDynamicDecimals,
  getShortAddress,
} from "../../factories/Utils";
import Bar from "../global/Bar.vue";

export default {
  name: "LoanBlock",
  components: {
    Block,
    RowText,
    Button,
    LoanModal,
    Loading,
    Bar,
  },

  props: {
    loan: { type: Object, required: true },
  },

  data() {
    return {
      isModalOpen: false,
      borrowOnly: false,
      depositOnly: false,
      payOnly: false,
      isLoading: false,
    };
  },

  computed: {
    ...mapState({
      borrowAPRRaw: ({ main }) =>
        (main.lendingPoolData &&
          main.lendingPoolData.calculateBorrowAPR) ||
        0,
      addressEnsMap: ({ main }) => main.addressEnsMap,
      ltvHealthThreshold: ({ main }) => main.ltvHealthThreshold,
      userAddy: ({ main }) => main.web3.address,
    }),
    ...mapGetters([
      "activeNetwork",
      "ethPriceUSDFixed",
      "nativeCurrencyWrappedAddress",
      "nativeCurrencySymbol",
    ]),

    borrowerText() {
      const owner = this.loan && this.loan.owner;
      if (typeof owner !== "string") return "0x";
      return (
        this.addressEnsMap[owner.toLowerCase()] ||
        getShortAddress(this.loan.owner) ||
        "0x"
      );
    },

    borrowerLink() {
      return (
        (this.activeNetwork &&
          this.loan &&
          this.loan.owner &&
          `${this.activeNetwork.explorer_url}/address/${this.loan.owner}`) ||
        ""
      );
    },

    chartLink() {
      return (
        (this.activeNetwork &&
          this.loan &&
          `${this.activeNetwork.chart_url}${this.loan.collateralPool}`) ||
        ""
      );
    },

    isExternalPool() {
      return (
        this.loan &&
        this.activeNetwork &&
        this.loan.lendingPoolContract.toLowerCase() ===
          this.activeNetwork.contracts.lendingPoolExt.toLowerCase()
      );
    },

    isLoanHealthy() {
      return this.ltvPerc
        ? new BigNumber(this.ltvPerc).lte(this.ltvHealthThreshold)
        : true;
    },

    loanTokenImg() {
      const symbol =
        (this.collateralTokenInfo &&
          this.collateralTokenInfo.symbol &&
          this.collateralTokenInfo.symbol.toLowerCase()) ||
        "hype";
      return `/img/${symbol}.png`;
    },

    depositAmountNoDecimals() {
      return (
        (this.loan &&
          new BigNumber(this.loan.amountDeposited)
            .div(
              new BigNumber(10).pow(this.collateralTokenInfo.decimals)
            )
            .toFixed()) ||
        0
      );
    },

    borrowAmountNoDecimals() {
      return (
        (this.loan &&
          new BigNumber(this.loan.amountETHBorrowed)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    doesUserOwnLoan() {
      return (
        this.userAddy &&
        typeof this.loan.owner === "string" &&
        this.userAddy.toLowerCase() === this.loan.owner.toLowerCase()
      );
    },

    hasBorrowedETH() {
      return new BigNumber(this.borrowAmountNoDecimals).gt(0);
    },

    collateralTokenInfo() {
      if (
        !(
          this.loan.collateralPoolInfo &&
          this.loan.collateralPoolInfo.token0
        )
      ) {
        return { symbol: "N/A" };
      }
      return this.loan.collateralPoolInfo.token0.address.toLowerCase() ===
        this.nativeCurrencyWrappedAddress.toLowerCase()
        ? this.loan.collateralPoolInfo.token1
        : this.loan.collateralPoolInfo.token0 || { symbol: "N/A" };
    },

    collateralAmountFixed() {
      return (
        (this.collateralTokenInfo &&
          new BigNumber(this.loan.amountDeposited)
            .div(
              new BigNumber(10).pow(this.collateralTokenInfo.decimals)
            )
            .toFixed()) ||
        "0"
      );
    },

    collateralAmountFormatted() {
      return formatDynamicDecimals(this.collateralAmountFixed);
    },

    collateralAmountUSDFormatted() {
      return new BigNumber(this.collateralAmountFixed)
        .times(this.loan.collateralPriceUSD)
        .toFormat(2);
    },

    borrowAmountFixed() {
      return new BigNumber(this.loan.amountETHBorrowed)
        .div(new BigNumber(10).pow(18))
        .toFixed();
    },

    borrowAmountFormatted() {
      return formatDynamicDecimals(this.borrowAmountFixed);
    },

    borrowAmountUSDFormatted() {
      return new BigNumber(this.borrowAmountFixed)
        .times(this.ethPriceUSDFixed)
        .toFormat(2);
    },

    feesFixed() {
      return new BigNumber(
        calculateAPRFees(
          this.borrowAPRRaw,
          this.loan.amountETHBorrowed,
          this.loan.aprStart
        )
      )
        .div(new BigNumber(10).pow(18))
        .toFixed();
    },

    feesFormatted() {
      return formatDynamicDecimals(this.feesFixed);
    },

    feesUSDFormatted() {
      return new BigNumber(this.feesFixed)
        .times(this.ethPriceUSDFixed)
        .toFormat(2);
    },

    ltvPerc() {
      return (
        (this.loan.ltv &&
          Number(new BigNumber(this.loan.ltv).times(100)).toFixed(
            2
          )) ||
        0
      );
    },
  },

  methods: {
    ...mapActions(["payBackLoan"]),

    openModal(borrow, deposit, pay, withdraw) {
      this.borrowOnly = borrow;
      this.depositOnly = deposit;
      this.payOnly = pay;
      this.withdrawOnly = withdraw;
      this.isModalOpen = true;
    },

    async closeLoan() {
      try {
        this.isLoading = true;
        await this.payBackLoan({
          tokenId: this.loan.tokenId,
          value: 1,
        });
        this.$notify({
          type: "success",
          text: `Successully closed loan!`,
        });
        this.isLoading = false;
      } catch (err) {
        this.$notify({
          type: "error",
          text: err.message,
        });
        console.error(`Error closing loan`, err);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
